import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import Grid from '@material-ui/core/Grid'
import {
  Card,
  CardContent,
  Box,
  CardHeader,
  Typography,
  makeStyles,
  Button,
  IconButton,
  Tab,
  Tabs,
  MenuItem,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import { Translate } from 'react-localize-redux'
import AlertDialog from 'components/AlertDialog'
import { lineups as lineupsModule, matches } from 'redux/modules'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import useTeamFeature from 'hooks/useTeamFeature'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import EditIcon from '@material-ui/icons/Edit'
import PublishIcon from '@material-ui/icons/Publish'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import TabPanel from 'components/material/TabPanel'
import DropDownMenu from 'components/DropDownMenu'
import CircularSpinner from 'components/loader/CircularSpinner'
import { toast } from 'react-toastify'
import FieldArea from './FieldArea'
import AddLineupDialog from './AddLineupDialog'
import PlayersListsContainer from './Team/PlayersListsContainer'
import RostersListContainer from './Roster'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useStyles = makeStyles((theme) => ({
  cardContent: {
    '&.MuiCardContent-root:last-child': {
      paddingBottom: 0,
    },
    padding: 0,
  },
  cardHeader: {
    '& .MuiCardHeader-action': {
      alignSelf: 'end',
    },
  },
  positionRelative: {
    position: 'relative',
  },
  gridOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.grey[100],
    opacity: '0.5',
    zIndex: 999,
  },
  playersCard: {
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  tabPanel: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 0,
    padding: 8,
    backgroundColor: theme.palette.common.white,
  },
  tabRoot: {
    '& .MuiTab-root': {
      padding: '6px 16px',
      minWidth: 'unset',
      marginLeft: 10,
    },
  },
}))

const LineUpContainer = ({
  lineups,
  participants,
  matchLineup,
  saveMatchLineup,
  updateMatch,
  currentMatchUuid,
  teamUuid,
}) => {
  const classes = useStyles()
  const [lineupPositions, setLineupPositions] = useState([])
  const [addLineupDialogOpen, setAddLineupDialogOpen] = useState(false)
  const [publishRosterAlertOpen, setPublishRosterAlertOpen] = useState(false)
  const [isEmptyLineup, setIsEmptyLineup] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [rostersList, setRostersList] = useState([])
  const [tabValue, setTabValue] = useState('roster')
  const [anchorEl, setAnchorEl] = useState(null)
  const [loading, setLoading] = useState(false)

  const featureGuard = useFeatureAccessGuard()

  const { enabled: hasAdvancedMatchFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_match_features_enable
  )

  const handleAddLineupDialog = () => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    const participantsOnField = participants.filter((p) => p.participant)
    if (participantsOnField.length === 0) {
      setIsEmptyLineup(true)

      return
    }

    setAddLineupDialogOpen(true)
  }

  const handleAddLineupDialogClose = () => {
    setAddLineupDialogOpen(false)
  }

  useEffect(() => {
    if (participants && matchLineup) {
      let tempPostions = []

      let tempLineup = lineups.find((l) => l.uuid === matchLineup.uuid)

      // #important
      // If tempLineup empty means match lineup is a saved lineup and its deleted by coach or management
      // But it will not affect matches which has this lineup selected so will assign it and use its positions
      if (!tempLineup && matchLineup.deleted_at) {
        tempLineup = matchLineup
      }

      tempPostions = tempLineup.lineup.map((position) => {
        const tolerance = 0.1
        const matchingPlayers = participants.filter((p) => {
          // Check difference of values because we have float value and string value in participant and lineup respectively
          // Eg: '56.80'(string) in lineup and 56.8(float) in participant so equality will not work here
          // Eg: '56.81'(string) in lineup and 56.82(float) in participant are almost same positions. Due to round off value might differ slightly
          // To avoid such cases fail we are using tolerance check here instead of simple equality check
          const xMatch =
            Math.abs(parseFloat(position.x) - parseFloat(p.position_x)) <
            tolerance
          const yMatch =
            Math.abs(parseFloat(position.y) - parseFloat(p.position_y)) <
            tolerance

          return (
            (xMatch && yMatch) ||
            // Player should be in saved lineup and participant in match to render on the field
            (position.player_uuid === p.player_uuid && p.participant)
          )
        })

        if (matchingPlayers.length > 0) {
          return {
            ...position,
            matchPlayer: matchingPlayers[0], // Array contains only one matching player
          }
        }

        return {
          x: position.x,
          y: position.y,
          player_uuid: position.player_uuid,
        }
      })
      setLineupPositions(tempPostions)
    }
  }, [participants, matchLineup])

  const handleSaveLineup = (values) => {
    saveMatchLineup(teamUuid, {
      match_uuid: currentMatchUuid,
      title: values.title,
    }).then((res) => {
      const { data } = res.response

      handleAddLineupDialogClose()

      // Update match lineup on new custom lineup save.
      // Also with this match lineup will automatically switched to new saved lineup in UI
      updateMatch(currentMatchUuid, {
        lineup_uuid: data.uuid,
        preventToastNotification: true,
      })
    })
  }

  const handleChange = (e, newValue) => {
    setTabValue(newValue)
  }

  const handleOptionsClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleCloseOptions = () => {
    setAnchorEl(null)
  }

  const handlePublishRosterConfirm = () => {
    setLoading(true)
    setPublishRosterAlertOpen(false)

    updateMatch(currentMatchUuid, {
      roster_published: 1,
      preventToastNotification: true,
    })
      .then(() => {
        toast.success('Roster published successfully')
        setLoading(false)
      })
      .catch(() => {
        toast.error('Something went wrong!')
        setLoading(false)
      })
  }

  const handlePublishRosterClick = () => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    setPublishRosterAlertOpen(true)
    setAnchorEl(null)
  }

  const handleEditRosterClick = () => {
    setTabValue('team')
    setAnchorEl(null)
  }

  useEffect(() => {
    setRostersList(participants.filter((p) => p.in_roster))
  }, [participants])

  return (
    <>
      {loading && <CircularSpinner />}

      {publishRosterAlertOpen && (
        <AlertDialog
          open={publishRosterAlertOpen}
          onClose={() => setPublishRosterAlertOpen(false)}
          onSuccess={handlePublishRosterConfirm}
          onCancel={() => setPublishRosterAlertOpen(false)}
          title={<Translate id="matches.publish-roster-alert-title" />}
          subTitle={<Translate id="matches.publish-roster-alert-subtitle" />}
          submitButtonLabel={<Translate id="matches.publish" />}
          cancelButtonLabel={<Translate id="button.cancel" />}
        />
      )}
      {isEmptyLineup && (
        <AlertDialog
          open={isEmptyLineup}
          onClose={() => setIsEmptyLineup(false)}
          onSuccess={() => setIsEmptyLineup(false)}
          onCancel={() => setIsEmptyLineup(false)}
          title={<Translate id="matches.save-lineup" />}
          subTitle={<Translate id="matches.empty-lineup-save-alert" />}
          submitButtonLabel={<Translate id="button.ok" />}
          cancelButtonLabel={<Translate id="button.close" />}
        />
      )}
      {addLineupDialogOpen && (
        <AddLineupDialog
          open={addLineupDialogOpen}
          handleClose={handleAddLineupDialogClose}
          onSubmit={handleSaveLineup}
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} xl={7} className={classes.positionRelative}>
          {isEditMode && <Box className={classes.gridOverlay} />}
          <Card className={classes.playersCard}>
            <CardHeader
              action={
                <>
                  <IconButton onClick={handleOptionsClick} size="small">
                    <MoreVertIcon />
                  </IconButton>
                  <DropDownMenu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    elevation={2}
                    keepMounted
                    edge="start"
                    open={Boolean(anchorEl)}
                    onClose={handleCloseOptions}
                    getContentAnchorEl={null}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={handleEditRosterClick}>
                      <EditIcon from fontSize="small" />
                      <Translate id="matches.edit-roster" />
                    </MenuItem>
                    <MenuItem onClick={handlePublishRosterClick}>
                      <PublishIcon fontSize="small" />
                      <Translate id="matches.publish-roster" />
                      {!hasAdvancedMatchFeaturesEnabled ? (
                        <LockIconComponent />
                      ) : null}
                    </MenuItem>
                  </DropDownMenu>
                </>
              }
              title={
                <Typography variant="h3">
                  <Translate id="drills.players" />
                </Typography>
              }
            />
            <CardContent className={classes.cardContent}>
              <Tabs
                value={tabValue}
                onChange={handleChange}
                indicatorColor="primary"
                className={classes.tabRoot}
              >
                <Tab label={<Translate id="matches.roster" />} value="roster" />
                <Tab label={<Translate id="team.team" />} value="team" />
              </Tabs>
              <TabPanel value={tabValue} index="roster" padding={0}>
                <Box className={classes.tabPanel}>
                  <RostersListContainer
                    players={rostersList}
                    setTabValue={setTabValue}
                  />
                </Box>
              </TabPanel>
              <TabPanel value={tabValue} index="team" padding={0}>
                <Box className={classes.tabPanel}>
                  <PlayersListsContainer players={participants} />
                </Box>
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} xl={5} container justifyContent="center">
          <Card className={classes.playersCard}>
            <CardHeader
              title={
                <Typography variant="h3">
                  <Translate id="matches.lineup" />
                </Typography>
              }
              action={
                <Button
                  onClick={handleAddLineupDialog}
                  variant="contained"
                  color="primary"
                  endIcon={
                    !hasAdvancedMatchFeaturesEnabled ? (
                      <LockIconComponent context="primaryButton" />
                    ) : null
                  }
                >
                  <Typography variant="inherit">
                    <Translate id="matches.save-lineup" />
                  </Typography>
                </Button>
              }
            />
            <CardContent className={classes.cardContent}>
              <FieldArea
                lineups={lineups}
                lineupPositions={lineupPositions}
                setLineupPositions={setLineupPositions}
                matchLineup={matchLineup}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

LineUpContainer.propTypes = {
  lineups: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  participants: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  matchLineup: PropTypes.shape().isRequired,
  saveMatchLineup: PropTypes.func.isRequired,
  updateMatch: PropTypes.func.isRequired,
  currentMatchUuid: PropTypes.string.isRequired,
  teamUuid: PropTypes.string.isRequired,
}

export default compose(
  connect(
    ({ matches: { current }, lineups: { items }, team }) => ({
      participants: current.relationships.participants,
      matchLineup: current.relationships.lineup,
      lineups: items,
      currentMatchUuid: current.uuid,
      teamUuid: team.uuid,
    }),
    {
      saveMatchLineup: lineupsModule.saveMatchLineup,
      updateMatch: matches.updateMatch,
    }
  )
)(LineUpContainer)
